import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { perfilamientoService } from '@/features/perfilamiento/services/perfilamiento.service'
import type {
  CompletePerfilamientoResponse,
  PerfilQuestionDto,
  PerfilProfileDto,
} from '@/features/perfilamiento/services/perfilamiento.types'
import {
  loadPerfilamientoQuestionnaireQuestions,
  persistPerfilamientoQuestionnaireQuestions,
} from '@/features/perfilamiento/store/perfilamiento.store'
import { Button } from '@/shared/components/ui/Button'
import { usePerfilamientoError } from '@/features/perfilamiento/components/PerfilamientoErrorModal'
import logoNegroSrc from '@/assets/LogoNegro.png'

type QuestionnaireLocationState = {
  questions?: PerfilQuestionDto[]
}

type ProcessState = {
  processId: string
  profile: PerfilProfileDto | null
  finalScore: number | null
  archetype: number | null
  questions: PerfilQuestionDto[]
}

const TIEBREAKER_CATEGORY = 5

export function PerfilamientoQuestionnairePage() {
  const { processId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('perfilamiento')
  const { showError, clearError } = usePerfilamientoError()
  const initialQuestionsRef = useRef<PerfilQuestionDto[]>(
    (location.state as QuestionnaireLocationState | null)?.questions ??
      (processId ? loadPerfilamientoQuestionnaireQuestions(processId) ?? [] : []),
  )
  const [process, setProcess] = useState<ProcessState | null>(() => {
    return processId
      ? {
          processId,
          profile: null,
          finalScore: null,
          archetype: null,
          questions: initialQuestionsRef.current,
        }
      : null
  })
  const [activeTiebreaker, setActiveTiebreaker] = useState<PerfilQuestionDto | null>(null)
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const loadRequestRef = useRef(0)
  const tiebreakerFallbackRef = useRef<PerfilQuestionDto | null>(null)

  async function finalizeIfComplete(currentProcessId: string, processToFinalize: ProcessState) {
    const unansweredQuestions = processToFinalize.questions.filter(
      (question) => question.selectedOptionId === null,
    )

    if (unansweredQuestions.length > 0) return false

    const completeResult = await perfilamientoService.complete(currentProcessId, i18n.language)

    if (!completeResult.success) {
      setError(completeResult.message)
      showError(completeResult.message, completeResult.errorCode)
      setIsLoading(false)
      return true
    }

    const payload = completeResult.data as CompletePerfilamientoResponse
    const tiebreakerQuestion = payload.tiebreakerQuestion ?? processToFinalize.questions.find(
      (question) => question.category === TIEBREAKER_CATEGORY,
    ) ?? null

    if (payload.needsTiebreaker && tiebreakerQuestion) {
      setProcess({
        ...processToFinalize,
        questions: processToFinalize.questions,
      })
      tiebreakerFallbackRef.current = tiebreakerQuestion
      setActiveTiebreaker(tiebreakerQuestion)
      setSelectedOption(null)
      setIsLoading(false)
      return true
    }

    navigate(`/perfilamiento/resultado/${currentProcessId}`, {
      replace: true,
      state: {
        profile: payload.profile,
        finalScore: payload.finalScore,
        archetype: payload.archetype,
      },
    })
    return true
  }

  useEffect(() => {
    if (!processId) return

    const currentProcessId = processId as string
    const requestId = ++loadRequestRef.current

    async function loadProcess() {
      setIsLoading(true)
      setError(null)

      const result = await perfilamientoService.getProceso(currentProcessId, i18n.language)

      // React StrictMode can invoke this effect twice in development. Ignore
      // any response from an older request so it cannot overwrite the active
      // tiebreaker selected by the latest response.
      if (requestId !== loadRequestRef.current) return

      if (!result.success) {
        setProcess(null)
        setError(result.message)
        showError(result.message, result.errorCode)
        setIsLoading(false)
        return
      }

      const nextProcess = result.data.process
      const nextQuestions = Array.isArray(nextProcess.questions) ? nextProcess.questions : []
      const persistedTiebreaker = nextQuestions.find(
        (question) => question.category === TIEBREAKER_CATEGORY,
      )
      const baseQuestions = nextQuestions.filter(
        (question) => question.category !== TIEBREAKER_CATEGORY,
      )
      const shouldResumeTiebreaker = Boolean(
        persistedTiebreaker &&
        baseQuestions.length > 0 &&
        baseQuestions.every((question) => question.selectedOptionId !== null),
      )

      if (nextProcess.status === 3 || (nextProcess.profile && nextQuestions.length === 0)) {
        navigate(`/perfilamiento/resultado/${currentProcessId}`, {
          replace: true,
          state: {
            profile: nextProcess.profile,
            finalScore: nextProcess.finalScore,
            archetype: nextProcess.archetype,
          },
        })
        return
      }

      if (nextProcess.status === 2 && nextQuestions.length > 0) {
        const handled = await finalizeIfComplete(currentProcessId, {
          processId: nextProcess.processId,
          profile: nextProcess.profile,
          finalScore: nextProcess.finalScore,
          archetype: nextProcess.archetype,
          questions: nextQuestions,
        })

        if (handled) return
      }

      if (nextQuestions.length === 0) {
        const fallbackQuestions = initialQuestionsRef.current

        if (fallbackQuestions.length > 0) {
          setProcess({
            processId: nextProcess.processId,
            profile: nextProcess.profile,
            finalScore: nextProcess.finalScore,
            archetype: nextProcess.archetype,
            questions: fallbackQuestions,
          })
          tiebreakerFallbackRef.current = null
          setActiveTiebreaker(null)
          setSelectedOption(null)
          setIsLoading(false)
          return
        }

        setProcess(null)
        setError(t('questionnaire.empty'))
        setIsLoading(false)
        return
      }

      setProcess({
        processId: nextProcess.processId,
        profile: nextProcess.profile,
        finalScore: nextProcess.finalScore,
        archetype: nextProcess.archetype,
        questions: nextQuestions,
      })
      persistPerfilamientoQuestionnaireQuestions(currentProcessId, nextQuestions)
      if (!shouldResumeTiebreaker) tiebreakerFallbackRef.current = null
      setActiveTiebreaker(shouldResumeTiebreaker ? persistedTiebreaker ?? null : null)
      setSelectedOption(null)
      setIsLoading(false)
    }

    void loadProcess()
  }, [clearError, i18n.language, navigate, processId, showError])

  const questionnaireQuestions = useMemo(
    () => process?.questions.filter((question) => question.category !== TIEBREAKER_CATEGORY) ?? [],
    [process],
  )

  const persistedPendingTiebreaker = useMemo(() => {
    if (activeTiebreaker) return null

    const candidate = process?.questions.find(
      (question) => question.category === TIEBREAKER_CATEGORY && question.selectedOptionId === null,
    )
    const baseQuestionsCompleted = questionnaireQuestions.length > 0 &&
      questionnaireQuestions.every((question) => question.selectedOptionId !== null)

    return baseQuestionsCompleted ? candidate ?? null : null
  }, [activeTiebreaker, process, questionnaireQuestions])

  useEffect(() => {
    if (!activeTiebreaker && persistedPendingTiebreaker) {
      tiebreakerFallbackRef.current = persistedPendingTiebreaker
      setActiveTiebreaker(persistedPendingTiebreaker)
      setSelectedOption(null)
    }
  }, [activeTiebreaker, persistedPendingTiebreaker])

  const activeQuestion = useMemo(() => {
    if (activeTiebreaker) return activeTiebreaker
    if (persistedPendingTiebreaker) return persistedPendingTiebreaker
    if (tiebreakerFallbackRef.current) return tiebreakerFallbackRef.current
    return questionnaireQuestions.find((question) => question.selectedOptionId === null) ?? null
  }, [activeTiebreaker, persistedPendingTiebreaker, questionnaireQuestions])

  // The tiebreaker is the fourth and final questionnaire question, not a
  // separate flow. It may already be present in the process after a reload,
  // so only add it to the total when it is still held locally.
  const hasActiveTiebreaker = Boolean(
    activeTiebreaker || persistedPendingTiebreaker || tiebreakerFallbackRef.current,
  )
  const totalQuestions = questionnaireQuestions.length + (hasActiveTiebreaker ? 1 : 0)
  const answeredQuestions = questionnaireQuestions.filter(
    (question) => question.selectedOptionId !== null,
  ).length
  const progressValue = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0
  const hasSelectionError = Boolean(error)
  const canGoBack = hasActiveTiebreaker ? (process?.questions.length ?? 0) > 0 : answeredQuestions > 0

  async function reloadProcess(nextProcessId: string) {
    const result = await perfilamientoService.getProceso(nextProcessId, i18n.language)

    if (!result.success) {
      setError(result.message)
      showError(result.message, result.errorCode)
      return false
    }

    const nextProcess = result.data.process
    const nextQuestions = Array.isArray(nextProcess.questions) ? nextProcess.questions : []
    const persistedTiebreaker = nextQuestions.find(
      (question) => question.category === TIEBREAKER_CATEGORY,
    )
    const baseQuestions = nextQuestions.filter(
      (question) => question.category !== TIEBREAKER_CATEGORY,
    )
    const shouldResumeTiebreaker = Boolean(
      persistedTiebreaker &&
      baseQuestions.length > 0 &&
      baseQuestions.every((question) => question.selectedOptionId !== null),
    )

    if (nextProcess.status === 3 || (nextProcess.profile && nextQuestions.length === 0)) {
      navigate(`/perfilamiento/resultado/${nextProcessId}`, {
        replace: true,
        state: {
          profile: nextProcess.profile,
          finalScore: nextProcess.finalScore,
          archetype: nextProcess.archetype,
        },
      })
      return true
    }

    if (nextProcess.status === 2 && nextQuestions.length > 0) {
      const handled = await finalizeIfComplete(nextProcessId, {
        processId: nextProcess.processId,
        profile: nextProcess.profile,
        finalScore: nextProcess.finalScore,
        archetype: nextProcess.archetype,
        questions: nextQuestions,
      })

      if (handled) return true
    }

    if (nextQuestions.length === 0) {
      const fallbackQuestions = initialQuestionsRef.current

      if (fallbackQuestions.length > 0) {
        setProcess({
          processId: nextProcess.processId,
          profile: nextProcess.profile,
          finalScore: nextProcess.finalScore,
          archetype: nextProcess.archetype,
          questions: fallbackQuestions,
        })
        persistPerfilamientoQuestionnaireQuestions(nextProcessId, fallbackQuestions)
        tiebreakerFallbackRef.current = null
        setSelectedOption(null)
        return true
      }

      setProcess(null)
      setError(t('questionnaire.empty'))
      return false
    }

    setProcess({
      processId: nextProcess.processId,
      profile: nextProcess.profile,
      finalScore: nextProcess.finalScore,
      archetype: nextProcess.archetype,
      questions: nextQuestions,
    })
    persistPerfilamientoQuestionnaireQuestions(nextProcessId, nextQuestions)
    if (!shouldResumeTiebreaker) tiebreakerFallbackRef.current = null
    setActiveTiebreaker(shouldResumeTiebreaker ? persistedTiebreaker ?? null : null)
    setSelectedOption(null)
    return true
  }

  async function handleSubmit() {
    if (!processId || !activeQuestion || !selectedOption) {
      setError(t('questionnaire.error.required'))
      return
    }

    if (!process) {
      await reloadProcess(processId)
      return
    }

    setIsSubmitting(true)
    setError(null)

    const answerResult = await perfilamientoService.answer(
      processId,
      {
        questionId: activeQuestion.questionId,
        optionId: selectedOption,
      },
      i18n.language,
    )

    if (!answerResult.success) {
      setIsSubmitting(false)
      setError(answerResult.message)
      showError(answerResult.message, answerResult.errorCode)
      return
    }


    if (activeQuestion.category === TIEBREAKER_CATEGORY) {
      tiebreakerFallbackRef.current = null
      const completeResult = await perfilamientoService.complete(processId, i18n.language)

      setIsSubmitting(false)

      if (!completeResult.success) {
        setError(completeResult.message)
        showError(completeResult.message, completeResult.errorCode)
        return
      }

      const payload = completeResult.data as CompletePerfilamientoResponse
      const tiebreakerQuestion = payload.tiebreakerQuestion ?? process.questions.find(
        (question) => question.category === TIEBREAKER_CATEGORY,
      ) ?? null

      if (payload.needsTiebreaker && tiebreakerQuestion) {
        clearError()
        tiebreakerFallbackRef.current = tiebreakerQuestion
        setActiveTiebreaker(tiebreakerQuestion)
        setSelectedOption(null)
        return
      }

      navigate(`/perfilamiento/resultado/${processId}`, {
        replace: true,
        state: {
          profile: payload.profile,
          finalScore: payload.finalScore,
          archetype: payload.archetype,
          justCompleted: true,
        },
      })
      return
    }

    const updatedQuestions = process?.questions.map((question) =>
      question.questionId === activeQuestion.questionId
        ? { ...question, selectedOptionId: selectedOption }
        : question,
    ) ?? []

    setProcess((current) => current
      ? {
          ...current,
          questions: current.questions.map((question) =>
            question.questionId === activeQuestion.questionId
              ? { ...question, selectedOptionId: selectedOption }
              : question,
          ),
        }
      : current)

    const hasUnansweredBaseQuestion = updatedQuestions
      .filter((question) => question.category !== TIEBREAKER_CATEGORY)
      .some((question) => question.selectedOptionId === null)

    if (hasUnansweredBaseQuestion) {
      setIsSubmitting(false)
      setSelectedOption(null)
      return
    }

    const completeResult = await perfilamientoService.complete(processId, i18n.language)

    setIsSubmitting(false)

    if (!completeResult.success) {
      setError(completeResult.message)
      showError(completeResult.message, completeResult.errorCode)
      return
    }

    const payload = completeResult.data as CompletePerfilamientoResponse
    const tiebreakerQuestion = payload.tiebreakerQuestion ?? process.questions.find(
      (question) => question.category === TIEBREAKER_CATEGORY,
    ) ?? null

    if (payload.needsTiebreaker && tiebreakerQuestion) {
      clearError()
      tiebreakerFallbackRef.current = tiebreakerQuestion
      setActiveTiebreaker(tiebreakerQuestion)
      setSelectedOption(null)
      return
    }

    navigate(`/perfilamiento/resultado/${processId}`, {
      replace: true,
      state: {
        profile: payload.profile,
        finalScore: payload.finalScore,
        archetype: payload.archetype,
        justCompleted: true,
      },
    })
  }

  function handleBack() {
    if (isSubmitting || !process) return

    setError(null)

    if (activeQuestion?.category === TIEBREAKER_CATEGORY) {
      const previousQuestion = questionnaireQuestions[questionnaireQuestions.length - 1]

      if (!previousQuestion) return

      setProcess({
        ...process,
        questions: process.questions.map((question) =>
          question.questionId === previousQuestion.questionId
            ? { ...question, selectedOptionId: null }
            : question,
        ),
      })
      tiebreakerFallbackRef.current = null
      setActiveTiebreaker(null)
      setSelectedOption(previousQuestion.selectedOptionId)
      return
    }

    if (answeredQuestions === 0) return

    const previousQuestionIndex = answeredQuestions - 1
    const previousQuestion = questionnaireQuestions[previousQuestionIndex]

    if (!previousQuestion) return

    setProcess({
        ...process,
      questions: process.questions.map((question) =>
          question.questionId === previousQuestion.questionId
            ? { ...question, selectedOptionId: null }
            : question,
        ),
    })
    setSelectedOption(previousQuestion.selectedOptionId)
  }

  return (
    <section className="flex h-[100svh] w-full justify-center overflow-y-auto px-4 py-4 md:px-8 md:py-6">
      <div className="flex w-full max-w-[848px] flex-col items-center">
        <div className="w-[200px] text-[var(--color-text-primary)] md:w-[286px]">
          <img alt="SynerGy" className="h-auto w-full" src={logoNegroSrc} />
        </div>

        {isLoading ? (
          <p className="mt-6 text-sm text-[var(--color-text-muted)]">{t('questionnaire.loading')}</p>
        ) : null}

        {!isLoading && isSubmitting && !activeQuestion ? (
          <p className="mt-6 text-sm text-[var(--color-text-muted)]">{t('questionnaire.processing')}</p>
        ) : null}

        {!isLoading && activeQuestion ? (
          <>
            <p className="mt-6 text-center text-[1.125rem] font-medium leading-[1.5rem] text-[#1a191a] md:mt-8 md:text-[1.375rem] md:leading-[1.875rem]">
              {t('questionnaire.progress', {
                current: activeTiebreaker ? totalQuestions : answeredQuestions + 1,
                total: totalQuestions,
              })}
            </p>

            <div className="mt-6 h-3 w-full rounded bg-[#f4f4f4] md:mt-8">
              <div
                className="h-3 rounded bg-[var(--color-brand-primary-active)] transition-[width] duration-300"
                style={{ width: `${activeTiebreaker ? 100 : Math.max(progressValue, 8)}%` }}
              />
            </div>

            <h1 className="mt-6 max-w-[52.8125rem] text-center text-[1.625rem] font-semibold leading-[2rem] text-[var(--color-text-primary)] md:mt-8 md:text-[2.75rem] md:leading-[3.375rem]">
              {activeQuestion.text}
            </h1>

            <div className="mt-6 flex w-full shrink-0 flex-col gap-4 md:mt-8 md:gap-5">
              {activeQuestion.options.map((option) => {
                const isSelected = selectedOption === option.optionId

                return (
                  <button
                    key={option.optionId}
                    className={[
                      'flex min-h-[3.75rem] w-full items-center justify-between rounded-lg border px-4 py-4 text-left transition-colors duration-150 md:min-h-[4rem] md:py-4',
                      isSelected
                        ? 'border-[var(--color-brand-primary)] bg-[var(--color-surface-accent-soft)]'
                        : hasSelectionError
                          ? 'border-[#e02d3c] bg-[#fff5f5]'
                          : 'border-transparent bg-[#f4f4f4]',
                      'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--color-focus-ring)]',
                    ].join(' ')}
                    onClick={() => {
                      setSelectedOption(option.optionId)
                      setError(null)
                    }}
                    type="button"
                  >
                    <span className="max-w-[calc(100%-2rem)] text-sm font-normal leading-[1.125rem] text-[#05141f] md:text-base md:leading-5">
                      {option.text}
                    </span>
                    <span
                      aria-hidden="true"
                      className={[
                        'inline-flex h-5 w-5 items-center justify-center rounded-full border bg-white transition-colors',
                        isSelected
                          ? 'border-[var(--color-brand-primary)]'
                          : hasSelectionError
                            ? 'border-[#e02d3c]'
                            : 'border-[#d7d7d7]',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'h-2.5 w-2.5 rounded-full transition-colors',
                          isSelected ? 'bg-[var(--color-brand-primary)]' : 'bg-transparent',
                        ].join(' ')}
                      />
                    </span>
                  </button>
                )
              })}

              {error ? (
                <p className="w-full text-left text-sm font-medium leading-5 text-[#e02d3c]">
                  {error}
                </p>
              ) : null}
            </div>

            <div className="mt-6 flex w-full max-w-[353px] items-center gap-3">
              {canGoBack ? (
                <Button
                  className="h-[48px] min-w-0 flex-1 rounded-[8px] px-3"
                  onClick={handleBack}
                  disabled={isSubmitting}
                  fullWidth={false}
                  variant="secondary"
                >
                  {t('questionnaire.back')}
                </Button>
              ) : null}
              <Button
                className={[
                  'h-[48px] min-w-0 rounded-[8px] px-3',
                  canGoBack ? 'flex-1' : 'w-full',
                ].join(' ')}
                onClick={() => void handleSubmit()}
                disabled={isSubmitting}
                fullWidth={false}
              >
                {t('questionnaire.next')}
              </Button>
            </div>
          </>
        ) : null}

        {!isLoading && !isSubmitting && !activeQuestion && !error ? (
          <div className="mt-6 w-full max-w-[720px] rounded-2xl border border-[#d9e0e7] bg-white px-6 py-5 text-sm text-[var(--color-text-muted)] shadow-[0_10px_30px_rgba(5,20,31,0.05)]">
            {t('questionnaire.empty')}
          </div>
        ) : null}
      </div>
    </section>
  )
}
